import { useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import useRedux from './useRedux';
import { getWorkspaceTabKey } from 'layouts/WorkspaceTabs/tabTitle';
import { updateWorkspaceTabUiState } from 'redux/workspaceTabs/actions';
import { WorkspaceTab } from 'redux/workspaceTabs/types';

type SetStateAction<T> = T | ((prev: T) => T);

/**
 * Persists a UI value on the current workspace tab (session-backed Redux).
 * Survives tab remount when switching away and back; cleared when the tab closes.
 */
const useWorkspaceTabUiState = <T,>(stateKey: string, defaultValue: T): [T, (next: SetStateAction<T>) => void] => {
  const location = useLocation();
  const { dispatch, appSelector } = useRedux();
  const tabKey = getWorkspaceTabKey(location.pathname, location.search);

  const value = appSelector((state) => {
    const tab = (state.WorkspaceTabs.tabs as WorkspaceTab[]).find((item) => item.key === tabKey);
    if (!tab?.uiState || !(stateKey in tab.uiState)) {
      return defaultValue;
    }
    return tab.uiState[stateKey] as T;
  });

  const valueRef = useRef(value);
  valueRef.current = value;

  const setValue = useCallback(
    (next: SetStateAction<T>) => {
      const resolved =
        typeof next === 'function' ? (next as (prev: T) => T)(valueRef.current) : next;
      dispatch(updateWorkspaceTabUiState(tabKey, { [stateKey]: resolved }));
    },
    [dispatch, stateKey, tabKey]
  );

  return [value, setValue];
};

export default useWorkspaceTabUiState;
